import {
  creativeMediaJobResponseSchema,
  creativeMediaProgressResponseSchema,
  creativeMediaSelectionResponseSchema,
} from "@/features/chat/creativeMediaSchemas";
import { creativeMediaSampleConflict, mergeCreativeMediaProgressSamples, type CreativeMediaProgressSample } from "./creativeMediaProgress";
import { baseApi } from "@/shared/api/baseApi";

export interface CreativeMediaJobScope {
  conversationId: string;
  jobId: string;
  organizationId: string;
  shopBindingId: string;
}

export interface ConversationCreativeMediaProgressInput {
  conversationId: string;
  organizationId: string;
  shopBindingId: string;
  userId: string;
  originTurnIds: readonly string[];
  jobIds: readonly string[];
}

interface ConversationCreativeMediaProgressResult {
  samples: CreativeMediaProgressSample[];
  protocolError?: boolean;
}

interface AcceptCreativeMediaSelectionInput extends CreativeMediaJobScope {
  expectedRevision: number;
  idempotencyKey: string;
  resourceId: string;
}

function creativeMediaJobPath(input: CreativeMediaJobScope, suffix = "") {
  const path = `/v1/organizations/${encodeURIComponent(input.organizationId)}/conversations/${encodeURIComponent(input.conversationId)}/creative-media/jobs/${encodeURIComponent(input.jobId)}${suffix}`;
  return `${path}?${new URLSearchParams({ shop_binding_id: input.shopBindingId }).toString()}`;
}

export const creativeMediaApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversationCreativeMediaProgress: builder.query<
      ConversationCreativeMediaProgressResult, ConversationCreativeMediaProgressInput
    >({
      keepUnusedDataFor: 0,
      // 分页与刷新复用一个会话订阅；来源变化强制读取，不创建第二套缓存。
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}:${JSON.stringify([queryArgs.userId, queryArgs.organizationId, queryArgs.shopBindingId, queryArgs.conversationId])}`,
      forceRefetch: () => true,
      merge: (current, incoming) => {
        current.protocolError = incoming.protocolError === true || incoming.samples.some((sample) => {
          const existing = current.samples.find((value) => value.job.job_id === sample.job.job_id);
          return existing && creativeMediaSampleConflict(existing, sample);
        });
        current.samples = mergeCreativeMediaProgressSamples(current.samples, incoming.samples);
      },
      async queryFn(input, api, _extraOptions, baseQuery) {
        const root = `/v1/organizations/${encodeURIComponent(input.organizationId)}/conversations/${encodeURIComponent(input.conversationId)}/creative-media/jobs`;
        const origins = [...new Set(input.originTurnIds)].sort();
        const batches: string[][] = [];
        for (let offset = 0; offset < origins.length; offset += 100) batches.push(origins.slice(offset, offset + 100));
        if (!batches.length) batches.push([]);
        let samples: CreativeMediaProgressSample[] = [];
        let protocolError = false;
        const readPage = async (url: string) => {
          const response = await baseQuery(url);
          if (response.error) return { error: response.error };
          const parsed = creativeMediaProgressResponseSchema.safeParse(response.data);
          if (!parsed.success || parsed.data.jobs.some((job) =>
            job.actor_user_id !== input.userId || job.organization_id !== input.organizationId || job.conversation_id !== input.conversationId)) {
            return { error: { status: "CUSTOM_ERROR" as const, error: "媒体状态暂未同步：读取合同异常" } };
          }
          for (const job of parsed.data.jobs) {
            const incoming = { job, serverNow: parsed.data.server_now, receivedAt: performance.now() };
            const existing = samples.find((sample) => sample.job.job_id === job.job_id);
            if (existing && creativeMediaSampleConflict(existing, incoming)) protocolError = true;
            samples = mergeCreativeMediaProgressSamples(samples, [incoming]);
          }
          return { page: parsed.data };
        };
        for (const batch of batches) {
          const visited = new Set<string>();
          let cursor: string | null = null;
          do {
            if (api.signal.aborted) return { error: { status: "CUSTOM_ERROR", error: "媒体状态读取已中止" } };
            const parameters = new URLSearchParams({ view: "progress", limit: "50" });
            parameters.set("shop_binding_id", input.shopBindingId);
            if (batch.length) parameters.set("origin_turn_ids", batch.join(","));
            if (cursor) parameters.set("cursor", cursor);
            const result = await readPage(`${root}?${parameters}`);
            if (result.error) return { error: result.error };
            cursor = result.page?.next_cursor ?? null;
            if (cursor && visited.has(cursor)) return { error: { status: "CUSTOM_ERROR", error: "媒体状态分页异常" } };
            if (cursor) visited.add(cursor);
          } while (cursor);
        }
        // 旧消息中的精确稳定引用可能没有来源元数据；只读详情补齐，绝不猜最新任务。
        for (const jobId of [...new Set(input.jobIds)]) {
          if (samples.some((sample) => sample.job.job_id === jobId)) continue;
          const parameters = new URLSearchParams({
            shop_binding_id: input.shopBindingId,
            view: "progress",
          });
          const result = await readPage(`${root}/${encodeURIComponent(jobId)}?${parameters}`);
          if (result.error) return { error: result.error };
          if (result.page?.has_more !== false || result.page.next_cursor !== null ||
              result.page.jobs.length !== 1 || result.page.jobs[0]?.job_id !== jobId) {
            return { error: { status: "CUSTOM_ERROR", error: "媒体任务引用无法确认" } };
          }
        }
        return { data: { samples, protocolError } };
      },
    }),
    acceptCreativeMediaSelection: builder.mutation<
      ReturnType<typeof creativeMediaSelectionResponseSchema.parse>,
      AcceptCreativeMediaSelectionInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: input.jobId, type: "CreativeMediaJob" },
        { id: input.jobId, type: "CreativeMediaSelection" },
      ],
      query: ({ expectedRevision, idempotencyKey, resourceId, ...scope }) => ({
        body: {
          expected_revision: expectedRevision,
          idempotency_key: idempotencyKey,
          resource_id: resourceId,
        },
        method: "PUT",
        url: creativeMediaJobPath(scope, "/selection"),
      }),
      transformResponse: (response) =>
        creativeMediaSelectionResponseSchema.parse(response),
    }),
    getCreativeMediaJob: builder.query<
      ReturnType<typeof creativeMediaJobResponseSchema.parse>,
      CreativeMediaJobScope
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.jobId, type: "CreativeMediaJob" },
        { id: input.jobId, type: "CreativeMediaSelection" },
      ],
      query: creativeMediaJobPath,
      transformResponse: (response) => creativeMediaJobResponseSchema.parse(response),
    }),
    getCreativeMediaSelection: builder.query<
      ReturnType<typeof creativeMediaSelectionResponseSchema.parse>,
      CreativeMediaJobScope
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.jobId, type: "CreativeMediaSelection" },
      ],
      query: (input) => creativeMediaJobPath(input, "/selection"),
      transformResponse: (response) =>
        creativeMediaSelectionResponseSchema.parse(response),
    }),
  }),
});

export const {
  useLazyGetConversationCreativeMediaProgressQuery,
  useAcceptCreativeMediaSelectionMutation,
  useGetCreativeMediaJobQuery,
  useGetCreativeMediaSelectionQuery,
} = creativeMediaApi;
